import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { SearchInput } from "components/input";
import { Button } from "components/button";
import useDebounce from "hooks/useDebounce";

/**
 * #34 (ESERV2-1434) — search the company's items from the review order view and
 * add one straight into this order, instead of walking back to the Order tab
 * mid-order. Search reuses the company-scoped purchasingItems endpoint; the
 * add goes through /addItemToOrder (ownership- and status-gated server side).
 */
const AddItemToOrder = ({ orderId, onAdded }) => {
  const { t } = useTranslation();
  const tBase = "views.purchasing.review.AddItemToOrder";
  const tr = (key) => t(`${tBase}.${key}`);

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [addingItemIds, setAddingItemIds] = useState(new Set());
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (debouncedSearch.trim().length < 2) {
        setResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/v2/eserv-business/purchasing/purchasingItems`,
          { params: { searchParam: debouncedSearch } }
        );
        if (!cancelled) {
          setResults((response.data.items || response.data || []).slice(0, 8));
        }
      } catch {
        if (!cancelled) {
          setResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  const addItem = async (item) => {
    if (addingItemIds.has(item.item_id)) {
      return;
    }
    setAddingItemIds((prev) => new Set(prev).add(item.item_id));
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/addItemToOrder`,
        { orderId, itemId: item.item_id, quantity: 1 }
      );
      if (response.data.alreadyInOrder) {
        toast.info(tr("Already in this order"));
      } else {
        toast.success(tr("Added to order").replace("{{item}}", item.item_name));
        setSearch("");
        setResults([]);
        if (onAdded) {
          onAdded();
        }
      }
    } catch (error) {
      const message = axios.isAxiosError?.(error)
        ? error.response?.data?.message
        : null;
      toast.error(message || tr("Could not add the item"));
    } finally {
      setAddingItemIds((prev) => {
        const next = new Set(prev);
        next.delete(item.item_id);
        return next;
      });
    }
  };

  return (
    <div className="add-item-to-order" style={{ position: "relative", margin: "8px 0" }}>
      <SearchInput
        placeholder={tr("Search items to add")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: 320 }}
      />
      {debouncedSearch.trim().length >= 2 && (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            width: 420,
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {isSearching ? (
            <div style={{ padding: 10, fontSize: 13, color: "#6b7280" }}>
              {tr("Searching...")}
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: 10, fontSize: 13, color: "#6b7280" }}>
              {tr("No items found")}
            </div>
          ) : (
            results.map((item) => (
              <div
                key={item.item_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "8px 10px",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{item.item_name}</div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>
                    {tr("In Stock")}: {item.in_stock ?? 0} · {tr("Outstanding")}:{" "}
                    {item.outstanding ?? 0}
                  </div>
                </div>
                <Button
                  size="small"
                  primary
                  disabled={addingItemIds.has(item.item_id)}
                  onClick={() => addItem(item)}
                >
                  {addingItemIds.has(item.item_id) ? tr("Adding...") : tr("Add")}
                </Button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AddItemToOrder;
